import type { BadgeVariant } from '../../components/ui/Badge';
import { chartTokens } from '../../styles/tokens';
import type { KhoaDich } from '../../i18n';
import type { TinhTrangGateway } from './nghiep-vu/agent';
import type { LoaiBuoc, LoaiHanhDongAgent, LoaiTaiKhoanGui, LoaiWorkflow, MucDo, TrangThaiTaskAgent } from './types';

/**
 * Mã quyền module — chép nguyên 33 mã của `modules/rpa/module.yaml` (ADR-0103 mục 3). Thiếu quyền thì ẩn nút
 * (ADR-UI-0005); thiếu quyền đọc thì màn hiện trạng thái khoá thay vì gọi API rồi nhận `403`. Máy chủ vẫn là chốt
 * cuối. `robotBao`, `clientDangNhap` chỉ cấp cho khoá API, không cấp cho người — khai để bảng khớp manifest.
 */
export const QUYEN = {
  tongQuanDoc: 'rpa:tong-quan:read',
  agentDoc: 'rpa:agent:read',
  agentTao: 'rpa:agent:create',
  agentChay: 'rpa:agent:execute',
  projectDoc: 'rpa:project:read',
  workflowDoc: 'rpa:workflow:read',
  workflowSua: 'rpa:workflow:update',
  workflowChay: 'rpa:workflow:run',
  lichChayDoc: 'rpa:lich-chay:read',
  lichChayTao: 'rpa:lich-chay:create',
  lichChaySua: 'rpa:lich-chay:update',
  lichChayXoa: 'rpa:lich-chay:delete',
  lichSuDoc: 'rpa:lich-su:read',
  lichSuSua: 'rpa:lich-su:update',
  suCoDoc: 'rpa:su-co:read',
  cauHinhLoiDoc: 'rpa:cau-hinh-loi:read',
  cauHinhLoiSua: 'rpa:cau-hinh-loi:update',
  thongBaoDoc: 'rpa:thong-bao:read',
  thongBaoSua: 'rpa:thong-bao:update',
  taiKhoanGuiDoc: 'rpa:tai-khoan-gui:read',
  taiKhoanGuiSua: 'rpa:tai-khoan-gui:update',
  licenseDoc: 'rpa:license:read',
  licenseTao: 'rpa:license:create',
  licenseSua: 'rpa:license:update',
  licenseXoa: 'rpa:license:delete',
  opencoreUserDoc: 'rpa:opencore-user:read',
  opencoreUserSua: 'rpa:opencore-user:update',
  opencoreRoleDoc: 'rpa:opencore-role:read',
  opencoreRoleSua: 'rpa:opencore-role:update',
  ketNoiDoc: 'rpa:ket-noi:read',
  ketNoiSua: 'rpa:ket-noi:update',
  robotBao: 'rpa:robot:report',
  clientDangNhap: 'rpa:client:login',
} as const;

/** Đường dẫn gốc (chưa ghép tiền tố cổng) của tab Kết nối OpenCore — đích của hướng dẫn "chưa khai kết nối". */
export const DUONG_KET_NOI = '/rpa/alert-config?tab=connection';

/** Robot chạy xong thì lượt gần nhất thôi `active`; hỏi lại mỗi 3 giây trong lúc chạy (như native). */
export const CHU_KY_LUOT_MS = 3_000;
/** Trạng thái Robot của tài khoản OpenCore đã kết nối (online/offline), như native. */
export const CHU_KY_LIEN_KET_MS = 30_000;

type BienThePhanLoai = Extract<BadgeVariant, 'teal' | 'violet' | 'orange' | 'info' | 'neutral'>;
type BienTheTrangThai = Extract<BadgeVariant, 'success' | 'warning' | 'danger' | 'primary' | 'neutral'>;

/**
 * Loại luồng — nhóm **phân loại** của `Badge`: OpenRPA hay Script là *loại*, không phải tốt/xấu. Native tô OpenRPA
 * màu `success` (đọc thành "đang khoẻ") — đổi sang `teal`.
 *
 * `vien`, `nen`, `chu` cho thẻ tổng, dòng đang chọn và tiêu đề nhóm ở danh sách bên trái. Viết đủ class (Tailwind
 * quét tĩnh).
 */
export const LOAI_LUONG: Record<
  LoaiWorkflow,
  { khoa: KhoaDich; tieuDe: KhoaDich; moTa: KhoaDich; variant: BienThePhanLoai; vien: string; vong: string; nen: string; chu: string }
> = {
  openrpa: {
    khoa: 'rpaWorkflow.loai.openrpa',
    tieuDe: 'rpaWorkflow.loai.openrpaTieuDe',
    moTa: 'rpaWorkflow.loai.openrpaMoTa',
    variant: 'teal',
    vien: 'border-teal-500',
    vong: 'ring-teal-500',
    nen: 'bg-teal-50',
    chu: 'text-teal-700',
  },
  script: {
    khoa: 'rpaWorkflow.loai.script',
    tieuDe: 'rpaWorkflow.loai.scriptTieuDe',
    moTa: 'rpaWorkflow.loai.scriptMoTa',
    variant: 'violet',
    vien: 'border-violet-500',
    vong: 'ring-violet-500',
    nen: 'bg-violet-50',
    chu: 'text-violet-700',
  },
};

/**
 * Mức độ của code lỗi — nhóm **trạng thái**: mức độ là nặng/nhẹ. Native tô `medium` bằng `info` (tông phân loại) —
 * đổi sang `primary`.
 */
export const MUC_DO_LOI: Record<MucDo, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  critical: { khoa: 'rpaLoi.mucDo.critical', variant: 'danger' },
  high: { khoa: 'rpaLoi.mucDo.high', variant: 'warning' },
  medium: { khoa: 'rpaLoi.mucDo.medium', variant: 'primary' },
  low: { khoa: 'rpaLoi.mucDo.low', variant: 'neutral' },
};

/** Tính chất của code: lường trước — nhấn `primary` (đã biết), bất ngờ xám. */
export const TINH_CHAT_LOI: Record<'luongTruoc' | 'batNgo', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  luongTruoc: { khoa: 'rpaLoi.code.luongTruoc', variant: 'primary' },
  batNgo: { khoa: 'rpaLoi.code.batNgo', variant: 'neutral' },
};

/** Số exception đang chờ — `warning`: đang chờ người xử lý. */
export const BADGE_DANG_CHO: BienTheTrangThai = 'warning';

/** Giá trị đã sửa mà chưa lưu — `warning`: chờ người bấm Lưu. */
export const BADGE_DA_SUA: BienTheTrangThai = 'warning';

/** Loại mục ở tab Biến & tham số — **phân loại**: tham số `info`, biến xám. */
export const LOAI_GIA_TRI: Record<'argument' | 'variable', BienThePhanLoai> = {
  argument: 'info',
  variable: 'neutral',
};

/** Loại một bản lưu ở lịch sử sửa — **phân loại**: sửa `info`, khôi phục `orange`. */
export const LOAI_BAN_LUU: Record<'edit' | 'restore', { khoa: KhoaDich; variant: BienThePhanLoai }> = {
  edit: { khoa: 'rpaGiaTri.banLuu.edit', variant: 'info' },
  restore: { khoa: 'rpaGiaTri.banLuu.restore', variant: 'orange' },
};

/**
 * Ô biểu tượng của từng loại bước trong danh sách bước — tông phân loại (không phải trạng thái: một bước rẽ nhánh
 * không "nguy hiểm" hơn bước thường).
 */
export const TONG_BUOC: Record<LoaiBuoc, string> = {
  start: 'bg-subtle text-content-secondary',
  end: 'bg-subtle text-content-secondary',
  activity: 'bg-subtle text-content-secondary',
  invoke: 'bg-primary-50 text-primary-700',
  decision: 'bg-orange-50 text-orange-700',
  loop: 'bg-sky-50 text-sky-700',
  try: 'bg-violet-50 text-violet-700',
  parallel: 'bg-teal-50 text-teal-700',
};

/** Bật/tắt của code, chi tiết lỗi khi người xem không có quyền sửa (thay cho công tắc) — nhóm **trạng thái**. */
export const BAT_TAT: Record<'bat' | 'tat', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  bat: { khoa: 'rpa.dangBat', variant: 'success' },
  tat: { khoa: 'rpa.dangTat', variant: 'neutral' },
};

/** Kết nối OpenCore của tenant — nhóm **trạng thái**. */
export const TRANG_THAI_KET_NOI: Record<'dangBat' | 'dangTat' | 'chuaKhai', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  dangBat: { khoa: 'rpa.dangBat', variant: 'success' },
  dangTat: { khoa: 'rpa.dangTat', variant: 'neutral' },
  chuaKhai: { khoa: 'rpaKetNoi.chuaKhai', variant: 'warning' },
};

/**
 * Trạng thái instance OpenRPA (`state` trên OpenCore) — nhóm **trạng thái**. Giá trị lạ hiện nguyên văn với tông xám
 * (`BadgeTrangThaiLuot`).
 */
export const TRANG_THAI_INSTANCE: Record<string, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  completed: { khoa: 'rpaLichSu.trangThai.completed', variant: 'success' },
  success: { khoa: 'rpaLichSu.trangThai.completed', variant: 'success' },
  failed: { khoa: 'rpaLichSu.trangThai.failed', variant: 'danger' },
  error: { khoa: 'rpaLichSu.trangThai.failed', variant: 'danger' },
  timeout: { khoa: 'rpaLichSu.trangThai.timeout', variant: 'danger' },
  running: { khoa: 'rpaLichSu.trangThai.running', variant: 'primary' },
  idle: { khoa: 'rpaLichSu.trangThai.idle', variant: 'neutral' },
  aborted: { khoa: 'rpaLichSu.trangThai.aborted', variant: 'warning' },
  cancelled: { khoa: 'rpaLichSu.trangThai.aborted', variant: 'warning' },
};

/** Mức của dòng log Robot. Viết đủ class (Tailwind quét tĩnh). */
export const MAU_DONG_LOG: Record<'error' | 'warning' | 'normal', string> = {
  error: 'text-danger-700',
  warning: 'text-warning-700',
  normal: 'text-content-primary',
};

/** Tình trạng đối chiếu theo số dòng — nhóm **trạng thái**: thiếu là lỗi, dư là cảnh báo. */
export const TINH_TRANG_SO_DONG: Record<'match' | 'missing' | 'extra' | 'empty', string> = {
  match: 'border-success-500 bg-success-50 text-success-700',
  missing: 'border-danger-500 bg-danger-50 text-danger-700',
  extra: 'border-warning-500 bg-warning-50 text-warning-700',
  empty: 'border-default bg-subtle text-content-secondary',
};

/**
 * Nguồn sự cố — nhóm **phân loại**: Robot, OpenCore hay FOXAI là *nơi* lỗi được ghi nhận, không phải nặng/nhẹ. Native
 * tô Robot màu `success` (đọc thành "đang khoẻ") và FOXAI màu `warning` — đổi sang tông phân loại.
 *
 * `mau` là màu loạt trên biểu đồ, cố định theo nguồn (không theo thứ hạng).
 */
export const NGUON_SU_CO_HIEN: Record<'foxai' | 'opencore' | 'robot', { khoa: KhoaDich; variant: BienThePhanLoai; mau: string }> = {
  foxai: { khoa: 'rpaSuCo.nguon.foxai', variant: 'orange', mau: chartTokens.cat1 },
  opencore: { khoa: 'rpaSuCo.nguon.opencore', variant: 'info', mau: chartTokens.cat2 },
  robot: { khoa: 'rpaSuCo.nguon.robot', variant: 'teal', mau: 'var(--color-teal-600)' },
};

/** Màu lát mức độ trên vành (đậm dần theo mức nặng; thang tự đảo ở nền tối). */
export const MAU_MUC_DO: Record<MucDo, string> = {
  critical: chartTokens.sevCritical,
  high: chartTokens.sevHigh,
  medium: chartTokens.sevMedium,
  low: chartTokens.sevLow,
};

/** Thanh "Phân bố nguồn lỗi": một màu, độ dài nói số lượng. */
export const MAU_PHAN_BO = 'var(--color-orange-600)';

// ─────────────── Dashboard RPA ───────────────

/**
 * Trạng thái Robot ở ô Tình trạng Robot — nhóm **trạng thái**. Nhãn lấy theo `status` qua i18n (không hiện
 * `statusLabel` tiếng Việt máy chủ trả, để đổi được ngôn ngữ).
 */
export const TRANG_THAI_ROBOT: Record<'running' | 'idle' | 'error' | 'unknown', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  running: { khoa: 'rpaTongQuan.robot.running', variant: 'primary' },
  idle: { khoa: 'rpaTongQuan.robot.idle', variant: 'success' },
  error: { khoa: 'rpaTongQuan.robot.error', variant: 'danger' },
  unknown: { khoa: 'rpaTongQuan.robot.unknown', variant: 'neutral' },
};

/**
 * Loạt của cột theo ngày và thanh theo Project: thành công, lỗi, đang chạy, bị dừng tay. Màu cố định theo nghĩa (không
 * theo thứ hạng), cùng thang token với các màn trạng thái.
 */
export const LOAT_LUOT: readonly { khoa: KhoaDich; mau: string }[] = [
  { khoa: 'rpaTongQuan.thanhCong', mau: 'var(--color-success-600)' },
  { khoa: 'rpaTongQuan.loi', mau: 'var(--color-danger-600)' },
  { khoa: 'rpaTongQuan.dangChay', mau: 'var(--color-primary-600)' },
  { khoa: 'rpaTongQuan.biDung', mau: 'var(--color-warning-500)' },
];

// ─────────────── License RPA ───────────────

/**
 * Trạng thái User License — nhóm **trạng thái**. `none` là User OpenCore chưa có bản ghi License; `pending` đang chờ
 * quản trị kích hoạt — cả hai xám như native.
 */
export const TRANG_THAI_LICENSE_HIEN: Record<
  'none' | 'pending' | 'active' | 'blocked' | 'expired' | 'revoked',
  { khoa: KhoaDich; variant: BienTheTrangThai }
> = {
  none: { khoa: 'rpaLicense.trangThai.none', variant: 'neutral' },
  pending: { khoa: 'rpaLicense.trangThai.pending', variant: 'neutral' },
  active: { khoa: 'rpaLicense.trangThai.active', variant: 'success' },
  blocked: { khoa: 'rpaLicense.trangThai.blocked', variant: 'danger' },
  expired: { khoa: 'rpaLicense.trangThai.expired', variant: 'warning' },
  revoked: { khoa: 'rpaLicense.trangThai.revoked', variant: 'danger' },
};

/**
 * Loại License — nhóm **phân loại**: Pro hay Limited là *gói quyền*, không phải tốt/xấu. Native tô Pro màu `warning`
 * (đọc thành "cần chú ý") — đổi sang `violet`.
 */
export const LOAI_LICENSE_HIEN: Record<'limited' | 'pro', { khoa: KhoaDich; ten: KhoaDich; moTa: KhoaDich; variant: BienThePhanLoai }> = {
  limited: { khoa: 'rpaLicense.loai.limitedNgan', ten: 'rpaLicense.loai.limited', moTa: 'rpaLicense.loai.limitedMoTa', variant: 'neutral' },
  pro: { khoa: 'rpaLicense.loai.proNgan', ten: 'rpaLicense.loai.pro', moTa: 'rpaLicense.loai.proMoTa', variant: 'violet' },
};

/** "Không thời hạn" — **phân loại** (một kiểu thời hạn, không phải trạng thái). */
export const BADGE_VINH_VIEN: BienThePhanLoai = 'info';

/** Quyền chạy thực tế trên OpenCore — nhóm **trạng thái**. */
export const TRUY_CAP_LICENSE: Record<
  'userDisabled' | 'syncError' | 'running' | 'syncing' | 'inactive',
  { khoa: KhoaDich; variant: BienTheTrangThai }
> = {
  userDisabled: { khoa: 'rpaLicense.truyCap.userDisabled', variant: 'danger' },
  syncError: { khoa: 'rpaLicense.truyCap.syncError', variant: 'danger' },
  running: { khoa: 'rpaLicense.truyCap.running', variant: 'success' },
  syncing: { khoa: 'rpaLicense.truyCap.syncing', variant: 'warning' },
  inactive: { khoa: 'rpaLicense.truyCap.inactive', variant: 'neutral' },
};

// ─────────────── User, Role OpenCore ───────────────

/** Trạng thái User — nhóm **trạng thái**: bị khoá đỏ, online xanh, offline xám. */
export const TRANG_THAI_USER_OC: Record<'disabled' | 'online' | 'offline', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  disabled: { khoa: 'rpaDanhBa.user.disabled', variant: 'danger' },
  online: { khoa: 'rpaDanhBa.user.online', variant: 'success' },
  offline: { khoa: 'rpaDanhBa.user.offline', variant: 'neutral' },
};

/** Đã/chưa xác minh — nhóm **trạng thái**: chưa xác minh là việc chờ làm. */
export const DA_XAC_MINH: Record<'co' | 'chua', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  co: { khoa: 'rpaDanhBa.user.co', variant: 'success' },
  chua: { khoa: 'rpaDanhBa.user.chua', variant: 'warning' },
};

/**
 * Nhãn role — nhóm **phân loại**: hệ thống, do License quản, RPA là *loại* role. Native tô role hệ thống `danger` và
 * role License `warning` (đọc thành "nguy hiểm", "cảnh báo") — đổi sang tông phân loại.
 */
export const NHAN_ROLE: Record<'system' | 'license' | 'rpa' | 'custom', { khoa: KhoaDich; variant: BienThePhanLoai }> = {
  system: { khoa: 'rpaDanhBa.role.heThong', variant: 'violet' },
  license: { khoa: 'rpaDanhBa.role.license', variant: 'orange' },
  rpa: { khoa: 'rpaDanhBa.role.rpa', variant: 'teal' },
  custom: { khoa: 'rpaDanhBa.role.tuyChinh', variant: 'neutral' },
};

/** Loại tài khoản gửi — **phân loại**: Gmail `orange`, Telegram `info`. Native tô Gmail `danger` (đọc thành "đang lỗi"). */
export const LOAI_TAI_KHOAN_HIEN: Record<LoaiTaiKhoanGui, { khoa: KhoaDich; moTa: KhoaDich; variant: BienThePhanLoai }> = {
  gmail: { khoa: 'rpaTaiKhoanGui.gmail', moTa: 'rpaTaiKhoanGui.gmailMoTa', variant: 'orange' },
  telegram: { khoa: 'rpaTaiKhoanGui.telegram', moTa: 'rpaTaiKhoanGui.telegramMoTa', variant: 'info' },
};

/** Kênh của quy tắc — **phân loại** `info`; kênh cũ (Teams, SMS) xám vì máy chủ không gửi qua. */
export const BADGE_KENH: BienThePhanLoai = 'info';
export const BADGE_KENH_CU: BienThePhanLoai = 'neutral';

/** Kênh đã có tài khoản gửi / biến môi trường chưa — **trạng thái**. */
export const KENH_CAU_HINH: Record<'co' | 'chua', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  co: { khoa: 'rpaThongBao.form.daCauHinh', variant: 'success' },
  chua: { khoa: 'rpaThongBao.form.chuaCauHinh', variant: 'warning' },
};

/** Phạm vi quy tắc khi xem theo Project — **phân loại**: riêng Project `info`, kế thừa xám. */
export const PHAM_VI_QUY_TAC: Record<'rieng' | 'keThua', { khoa: KhoaDich; variant: BienThePhanLoai }> = {
  rieng: { khoa: 'rpaThongBao.riengProject', variant: 'info' },
  keThua: { khoa: 'rpaThongBao.keThua', variant: 'neutral' },
};

/** Tình trạng Agent Gateway — **trạng thái**. */
export const GATEWAY_HIEN: Record<TinhTrangGateway, { khoa: KhoaDich; goiY: KhoaDich | null; variant: BienTheTrangThai }> = {
  daKetNoi: { khoa: 'rpaAgent.ketNoi.daKetNoi', goiY: null, variant: 'success' },
  khongKetNoi: { khoa: 'rpaAgent.ketNoi.khongKetNoi', goiY: 'rpaAgent.ketNoi.khongKetNoiGoiY', variant: 'warning' },
  chuaCauHinh: { khoa: 'rpaAgent.ketNoi.chuaCauHinh', goiY: 'rpaAgent.ketNoi.chuaCauHinhGoiY', variant: 'danger' },
};

/** Trạng thái task agent — **trạng thái**. */
export const TRANG_THAI_TASK: Record<TrangThaiTaskAgent, { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  running: { khoa: 'rpaAgent.trangThai.running', variant: 'primary' },
  completed: { khoa: 'rpaAgent.trangThai.completed', variant: 'success' },
  failed: { khoa: 'rpaAgent.trangThai.failed', variant: 'danger' },
};

/** Loại hành động agent đề xuất — **phân loại**. */
export const LOAI_HANH_DONG: Record<LoaiHanhDongAgent, { khoa: KhoaDich; variant: BienThePhanLoai }> = {
  retry_run: { khoa: 'rpaAgent.hanhDong.retry_run', variant: 'orange' },
  run_workflow: { khoa: 'rpaAgent.hanhDong.run_workflow', variant: 'teal' },
  check_robot: { khoa: 'rpaAgent.hanhDong.check_robot', variant: 'info' },
  review_workflow: { khoa: 'rpaAgent.hanhDong.review_workflow', variant: 'violet' },
};

/** Kết quả thực thi một hành động — **trạng thái**. */
export const THUC_THI_HIEN: Record<'done' | 'failed', { khoa: KhoaDich; variant: BienTheTrangThai }> = {
  done: { khoa: 'rpaAgent.thucThi.daXong', variant: 'success' },
  failed: { khoa: 'rpaAgent.thucThi.loi', variant: 'danger' },
};
